import { NewsAnnotationService } from 'src/app/services/news-annotation-service';
import { UrgentAlertTemplateService } from 'src/app/services/urgent-alert-template-service';
import { BehaviorSubject } from 'rxjs';
import { Injectable } from '@angular/core';
import { TodoItemNode } from '../models/todo-item-node';
/**
 * Checklist database, it can build a tree structured Json object.
 * Each node in Json object represents a to-do item or a category.
 * If a node is a category, it has children items and new items can be added under the category.
 */
@Injectable()
export class ChecklistDatabaseService {

  private urgentAlertTemplates = [];
  private newsAnnotations = [];

  dataGlobalChange = new BehaviorSubject<TodoItemNode[]>([]);
  get dataGlobal(): TodoItemNode[] { return this.dataGlobalChange.value; }

  dataCardiacChange = new BehaviorSubject<TodoItemNode[]>([]);
  get dataCardiac(): TodoItemNode[] { return this.dataCardiacChange.value; }

  dataTechChange = new BehaviorSubject<TodoItemNode[]>([]);
  get dataTech(): TodoItemNode[] { return this.dataTechChange.value; }

  dataNewsChange = new BehaviorSubject<TodoItemNode[]>([]);
  get dataNews(): TodoItemNode[] { return this.dataNewsChange.value; }

  dataAutomaticChange = new BehaviorSubject<TodoItemNode[]>([]);
  get dataAutomatic(): TodoItemNode[] { return this.dataAutomaticChange.value; }

  constructor(
    private urgentAlertTemplateService: UrgentAlertTemplateService,
    private newsAnnotationService: NewsAnnotationService,
  ) {
    this.initialize();
  }

  getUrgentAlertTemplates() {
    return this.urgentAlertTemplates;
  }

  getNewsAnnotations() {
    return this.newsAnnotations;
  }

  async initialize() {
    const globalData = this.getGlobalData();
    this.dataGlobalChange.next(this.buildFileTree(globalData, 0, 'global-cardiac'));

    this.urgentAlertTemplates = await this.urgentAlertTemplateService.getTemplates().toPromise();
    this.newsAnnotations = await this.newsAnnotationService.getNewsAnnotations().toPromise();
    const urgentTemplatesCardiac = [];
    this.urgentAlertTemplates.forEach(e => {
      if (e.UrgentAlertTemplateType == 'CARDIAC' && !e.UrgentAlertTemplateParentID) {
        urgentTemplatesCardiac.push(this.getUrgTemplItemData(e));
      }
    });
    this.dataCardiacChange.next(this.buildFileTree(urgentTemplatesCardiac, 0, 'cardiac'));

    const urgentTemplatesTech = [];
    this.urgentAlertTemplates.forEach(e => {
      if (e.UrgentAlertTemplateType == 'TECH' && !e.UrgentAlertTemplateParentID) {
        urgentTemplatesTech.push(this.getUrgTemplItemData(e));
      }
    });
    this.dataTechChange.next(this.buildFileTree(urgentTemplatesTech, 0, 'tech'));

    const urgentTemplatesNews = [];
    this.urgentAlertTemplates.forEach(e => {
      if (e.UrgentAlertTemplateType == 'NEWS' && !e.UrgentAlertTemplateParentID) {
        urgentTemplatesNews.push(this.getUrgTemplItemData(e));
      }
    });
    this.dataNewsChange.next(this.buildFileTree(urgentTemplatesNews, 0, 'news'));

    const automatic = [];
    this.newsAnnotations.forEach(e => {
      automatic.push(this.getNewsAnnotItemData(e));
    });
    this.dataAutomaticChange.next(this.buildFileTree(automatic, 0, 'automatic'));
  }

  getGlobalData() {
    return [
      { title: 'Cardiac', key: 'CARDIAC', children: [] },
      { title: 'Tech', key: 'TECH', children: [] },
      { title: 'News', key: 'NEWS', children: [] },
      { title: 'Automatic', key: 'AUTOMATIC', children: [] },
    ];
  }



  getUrgTemplItemData(data) {
    const name = data.UrgentAlertTemplateTranslationName.replace(new RegExp('%v%', 'g'), '...');
    const children = this.getUrgTemplateChildData(data.UrgentAlertTemplateID);
    return { title: name, key: data.UrgentAlertTemplateID, children: children };
  }

  getUrgTemplateChildData(parent) {
    const childData = [];
    const children = this.urgentAlertTemplates.filter(x => x.UrgentAlertTemplateParentID == parent);
    children.forEach(e => {
      childData.push(this.getUrgTemplItemData(e));
    });
    return childData;
  }

  getNewsAnnotItemData(data) {
    const children = this.getNewsAnnotChildData(data.NewsAnnotationID);
    return { title: data.NewsAnnotationTranslationName, key: data.NewsAnnotationID, children: children };
  }

  getNewsAnnotChildData(parent) {
    const childData = [];
    const children = this.newsAnnotations.filter(x => x.NewsAnnotationParentID == parent);
    children.forEach(e => {
      childData.push(this.getNewsAnnotItemData(e));
    });
    return childData;
  }

  /**
   * Build the file structure tree. The `value` is the Json object, or a sub-tree of a Json object.
   * The return value is the list of `TodoItemNode`.
   */
  buildFileTree(data: Array<any>, level: number, type: string): TodoItemNode[] {
    return data.reduce<TodoItemNode[]>((accumulator, key) => {
      const value = key.title;
      const node = new TodoItemNode();
      node.item = value;
      node.id = key.key;
      node.type = type;

      if (key.children.length) {
        node.children = this.buildFileTree(key.children, level + 1, type);
      }
      return accumulator.concat(node);
    }, []);
  }
}